// C:\IntegraNube_WebStore\src\pages\Categoria.js

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Link as RouterLink,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Divider,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';

import {
  ArrowBackOutlined as BackIcon,
  CategoryOutlined as CategoryIcon,
  CloseOutlined as CloseIcon,
  FilterAltOutlined as FilterIcon,
  Inventory2Outlined as ProductIcon,
  RestartAltOutlined as ResetIcon,
  SearchOutlined as SearchIcon,
  StarRounded as StarIcon,
  TuneOutlined as TuneIcon,
} from '@mui/icons-material';

import api from '../api/api';

/* ===================================================================== */
/*  Constantes                                                           */
/* ===================================================================== */

const PAGE_LIMIT = 12;

const DEFAULT_FILTERS = Object.freeze({
  q: '',
  marcaId: '',
  precioMin: '',
  precioMax: '',
  disponible: false,
  destacado: false,
  orden: 'relevancia',
  page: 1,
});

/* ===================================================================== */
/*  Helpers generales                                                    */
/* ===================================================================== */

function cleanString(value) {
  const result = String(value ?? '').trim();
  return result || '';
}

function resolveTiendaData(source) {
  if (!source || typeof source !== 'object') {
    return {};
  }

  if (
    source.status &&
    source.data &&
    typeof source.data === 'object'
  ) {
    return source.data;
  }

  if (
    source.data &&
    typeof source.data === 'object' &&
    (
      source.data.branding ||
      source.data.nombre
    )
  ) {
    return source.data;
  }

  return source;
}

function boolFromQuery(value) {
  return ['1', 'true', 'si', 'sí', 'yes'].includes(
    String(value || '')
      .trim()
      .toLowerCase()
  );
}

function positivePage(value) {
  const number = Number.parseInt(value, 10);

  return Number.isInteger(number) && number > 0
    ? number
    : 1;
}

function readFilters(searchParams) {
  return {
    q:
      searchParams.get('q') || '',

    marcaId:
      searchParams.get('marca_id') || '',

    precioMin:
      searchParams.get('precio_min') || '',

    precioMax:
      searchParams.get('precio_max') || '',

    disponible:
      boolFromQuery(
        searchParams.get('disponible')
      ),

    destacado:
      boolFromQuery(
        searchParams.get('destacado')
      ),

    orden:
      searchParams.get('orden') ||
      'relevancia',

    page:
      positivePage(
        searchParams.get('page')
      ),
  };
}

function filtersToSearchParams(filters) {
  const params = new URLSearchParams();

  const q = cleanString(filters.q);

  if (q) {
    params.set('q', q);
  }

  if (filters.marcaId) {
    params.set(
      'marca_id',
      String(filters.marcaId)
    );
  }

  if (
    filters.precioMin !== '' &&
    filters.precioMin !== null
  ) {
    params.set(
      'precio_min',
      String(filters.precioMin)
    );
  }

  if (
    filters.precioMax !== '' &&
    filters.precioMax !== null
  ) {
    params.set(
      'precio_max',
      String(filters.precioMax)
    );
  }

  if (filters.disponible) {
    params.set('disponible', '1');
  }

  if (filters.destacado) {
    params.set('destacado', '1');
  }

  if (
    filters.orden &&
    filters.orden !== 'relevancia'
  ) {
    params.set(
      'orden',
      filters.orden
    );
  }

  if (Number(filters.page) > 1) {
    params.set(
      'page',
      String(filters.page)
    );
  }

  return params;
}

/* ===================================================================== */
/*  Helpers API                                                          */
/* ===================================================================== */

function extractCategory(payload) {
  if (
    payload?.data &&
    !Array.isArray(payload.data) &&
    typeof payload.data === 'object'
  ) {
    return payload.data;
  }

  if (
    payload &&
    !Array.isArray(payload) &&
    typeof payload === 'object' &&
    payload.id
  ) {
    return payload;
  }

  return null;
}

function extractProducts(payload) {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.productos)) {
    return payload.productos;
  }

  if (Array.isArray(payload?.data?.productos)) {
    return payload.data.productos;
  }

  return [];
}

function extractFilters(payload) {
  if (
    payload?.data &&
    typeof payload.data === 'object' &&
    !Array.isArray(payload.data)
  ) {
    return payload.data;
  }

  return payload || {};
}

/* ===================================================================== */
/*  Helpers producto                                                     */
/* ===================================================================== */

function formatPrice(
  value,
  moneda = 'CLP'
) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return null;
  }

  try {
    return new Intl.NumberFormat(
      'es-CL',
      {
        style: 'currency',
        currency: moneda || 'CLP',
        maximumFractionDigits:
          moneda === 'CLP'
            ? 0
            : 2,
      }
    ).format(number);
  } catch (_) {
    return (
      '$' +
      Math.round(number)
        .toLocaleString('es-CL')
    );
  }
}

function getProductImage(product) {
  const candidates = [
    product?.imagen,
    product?.imagen_url,
    product?.imagenUrl,
    product?.imagen_publica,
    product?.primera_imagen_publica,
    product?.thumbnail,
  ];

  for (const candidate of candidates) {
    if (
      typeof candidate === 'string' &&
      candidate.trim()
    ) {
      return candidate.trim();
    }
  }

  if (
    Array.isArray(product?.imagenes) &&
    product.imagenes.length
  ) {
    const first = product.imagenes[0];

    if (
      typeof first === 'string' &&
      first.trim()
    ) {
      return first.trim();
    }

    if (
      first &&
      typeof first === 'object'
    ) {
      return (
        first.url ||
        first.url_publica ||
        first.src ||
        null
      );
    }
  }

  return null;
}

function getProductPath(product) {
  const slug = cleanString(product?.slug);

  if (!slug) {
    return '/productos';
  }

  return `/producto/${encodeURIComponent(slug)}`;
}

/* ===================================================================== */
/*  ProductCard                                                          */
/* ===================================================================== */

function ProductCard({
  product,
  colorPrimario,
  mostrarStock,
  mostrarSku,
}) {
  const [imageError, setImageError] =
    useState(false);

  const imageUrl =
    getProductImage(product);

  useEffect(() => {
    setImageError(false);
  }, [imageUrl]);

  const nombre =
    cleanString(product?.nombre) ||
    'Producto';

  const sku =
    cleanString(product?.sku);

  const moneda =
    product?.moneda || 'CLP';

  const precioValue =
    product?.precio ??
    product?.precio_bruto ??
    product?.precioNeto ??
    product?.precio_neto;

  const precio =
    formatPrice(
      precioValue,
      moneda
    );

  const enOferta =
    product?.enOferta === true ||
    product?.en_oferta === true;

  const precioNormalValue =
    product?.precioNormal ??
    product?.precio_normal;

  const precioNormal =
    enOferta &&
    Number(precioNormalValue) >
      Number(precioValue)
      ? formatPrice(
          precioNormalValue,
          moneda
        )
      : null;

  const descuento =
    Math.round(
      Number(
        product?.descuentoPorcentaje ??
        product?.descuento_porcentaje ??
        0
      )
    );

  const stockNumber =
    Number(product?.stock);

  const stockKnown =
    product?.stock !== null &&
    product?.stock !== undefined &&
    Number.isFinite(stockNumber);

  const disponible =
    product?.disponible !== false;

  const destacado =
    product?.destacado === true;

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        border: '1px solid #e5e7eb',
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: '#fff',
        transition:
          'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',

        '&:hover': {
          transform: 'translateY(-3px)',
          borderColor: '#d1d5db',
          boxShadow:
            '0 16px 36px rgba(15,23,42,0.08)',
        },
      }}
    >
      <CardActionArea
        component={RouterLink}
        to={getProductPath(product)}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: '1 / 1',
            bgcolor: '#f8fafc',
            overflow: 'hidden',
          }}
        >
          {imageUrl && !imageError ? (
            <CardMedia
              component="img"
              image={imageUrl}
              alt={nombre}
              loading="lazy"
              onError={() =>
                setImageError(true)
              }
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                p: 1.5,
              }}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'grid',
                placeItems: 'center',
                color: '#9ca3af',
              }}
            >
              <ProductIcon
                sx={{
                  fontSize: 58,
                  opacity: 0.32,
                }}
              />
            </Box>
          )}

          {destacado ? (
            <Chip
              icon={
                <StarIcon
                  sx={{
                    fontSize:
                      '16px !important',
                  }}
                />
              }
              label="Destacado"
              size="small"
              sx={{
                position: 'absolute',
                top: 12,
                left: 12,
                bgcolor: '#fff',
                color: colorPrimario,
                fontWeight: 800,
                border:
                  `1px solid ${colorPrimario}30`,
                boxShadow:
                  '0 4px 12px rgba(15,23,42,0.06)',
              }}
            />
          ) : null}

          {enOferta && descuento > 0 ? (
            <Chip
              label={`-${descuento}%`}
              size="small"
              sx={{
                position: 'absolute',
                bottom: 12,
                left: 12,
                bgcolor: '#dc2626',
                color: '#fff',
                fontWeight: 900,
              }}
            />
          ) : null}

          {!disponible ? (
            <Chip
              label="Agotado"
              size="small"
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                bgcolor:
                  'rgba(17,24,39,0.88)',
                color: '#fff',
                fontWeight: 800,
              }}
            />
          ) : null}
        </Box>

        <CardContent
          sx={{
            width: '100%',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
          }}
        >
          {mostrarSku && sku ? (
            <Typography
              variant="caption"
              sx={{
                mb: 0.5,
                color: '#9ca3af',
                fontWeight: 700,
              }}
            >
              SKU {sku}
            </Typography>
          ) : null}

          {product?.marca?.nombre ? (
            <Typography
              variant="caption"
              sx={{
                mb: 0.4,
                color: '#6b7280',
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '0.035em',
              }}
            >
              {product.marca.nombre}
            </Typography>
          ) : null}

          <Typography
            sx={{
              color: '#111827',
              fontWeight: 800,
              lineHeight: 1.35,
              minHeight: 44,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {nombre}
          </Typography>

          <Box sx={{ flex: 1 }} />

          {precioNormal ? (
            <Typography
              variant="body2"
              sx={{
                mt: 1.4,
                color: '#9ca3af',
                textDecoration: 'line-through',
                fontWeight: 700,
              }}
            >
              {precioNormal}
            </Typography>
          ) : null}

          {precio ? (
            <Typography
              sx={{
                mt: precioNormal ? 0.2 : 1.5,
                color:
                  disponible
                    ? colorPrimario
                    : '#6b7280',
                fontSize: 21,
                lineHeight: 1.2,
                fontWeight: 900,
              }}
            >
              {precio}
            </Typography>
          ) : null}

          {mostrarStock && stockKnown ? (
            <Typography
              variant="caption"
              sx={{
                mt: 0.8,
                color:
                  stockNumber > 0
                    ? '#059669'
                    : '#9ca3af',
                fontWeight: 700,
              }}
            >
              {stockNumber > 0
                ? `${stockNumber.toLocaleString(
                    'es-CL'
                  )} disponible${
                    stockNumber === 1
                      ? ''
                      : 's'
                  }`
                : 'Sin stock'}
            </Typography>
          ) : null}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

/* ===================================================================== */
/*  Panel de filtros                                                     */
/* ===================================================================== */

function FiltersPanel({
  filters,
  filterData,
  localPrecioMin,
  localPrecioMax,
  setLocalPrecioMin,
  setLocalPrecioMax,
  updateFilter,
  applyPrice,
  resetFilters,
  onClose = null,
}) {
  const marcas =
    Array.isArray(filterData?.marcas)
      ? filterData.marcas
      : [];

  const destacados =
    Number(
      filterData?.destacados || 0
    );

  const precioMinGlobal =
    Number(
      filterData?.precio?.min || 0
    );

  const precioMaxGlobal =
    Number(
      filterData?.precio?.max || 0
    );

  return (
    <Box sx={{ width: '100%' }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
        >
          <TuneIcon
            sx={{ color: '#4b5563' }}
          />

          <Typography
            sx={{
              fontWeight: 900,
              fontSize: 18,
              color: '#111827',
            }}
          >
            Filtros
          </Typography>
        </Stack>

        {onClose ? (
          <IconButton
            onClick={onClose}
            aria-label="Cerrar filtros"
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </Stack>

      <Stack spacing={2.25}>
        {marcas.length > 0 ? (
          <FormControl
            fullWidth
            size="small"
          >
            <InputLabel>Marca</InputLabel>

            <Select
              value={filters.marcaId}
              label="Marca"
              onChange={(event) =>
                updateFilter(
                  'marcaId',
                  event.target.value
                )
              }
            >
              <MenuItem value="">
                Todas
              </MenuItem>

              {marcas.map((marca) => (
                <MenuItem
                  key={marca.id}
                  value={String(marca.id)}
                >
                  {marca.nombre}
                  {Number(marca.productos) > 0
                    ? ` (${Number(marca.productos).toLocaleString('es-CL')})`
                    : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}

        <Divider />

        <Box>
          <Typography
            variant="body2"
            sx={{
              mb: 1.25,
              fontWeight: 900,
              color: '#374151',
            }}
          >
            Rango de precio
          </Typography>

          <Stack
            direction="row"
            spacing={1}
          >
            <TextField
              value={localPrecioMin}
              onChange={(event) =>
                setLocalPrecioMin(
                  event.target.value.replace(
                    /[^0-9]/g,
                    ''
                  )
                )
              }
              placeholder={
                precioMinGlobal > 0
                  ? Math.floor(precioMinGlobal)
                      .toLocaleString('es-CL')
                  : 'Mínimo'
              }
              size="small"
              fullWidth
              inputProps={{
                inputMode: 'numeric',
                'aria-label': 'Precio mínimo',
              }}
            />

            <TextField
              value={localPrecioMax}
              onChange={(event) =>
                setLocalPrecioMax(
                  event.target.value.replace(
                    /[^0-9]/g,
                    ''
                  )
                )
              }
              placeholder={
                precioMaxGlobal > 0
                  ? Math.ceil(precioMaxGlobal)
                      .toLocaleString('es-CL')
                  : 'Máximo'
              }
              size="small"
              fullWidth
              inputProps={{
                inputMode: 'numeric',
                'aria-label': 'Precio máximo',
              }}
            />
          </Stack>

          <Button
            fullWidth
            variant="outlined"
            onClick={applyPrice}
            sx={{
              mt: 1,
              textTransform: 'none',
              fontWeight: 800,
            }}
          >
            Aplicar precio
          </Button>
        </Box>

        <Divider />

        <Stack spacing={0.6}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 800,
                color: '#374151',
              }}
            >
              Sólo disponibles
            </Typography>

            <Switch
              checked={filters.disponible}
              onChange={(event) =>
                updateFilter(
                  'disponible',
                  event.target.checked
                )
              }
            />
          </Stack>

          {destacados > 0 ? (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 800,
                  color: '#374151',
                }}
              >
                Sólo destacados
              </Typography>

              <Switch
                checked={filters.destacado}
                onChange={(event) =>
                  updateFilter(
                    'destacado',
                    event.target.checked
                  )
                }
              />
            </Stack>
          ) : null}
        </Stack>

        <Button
          startIcon={<ResetIcon />}
          onClick={resetFilters}
          sx={{
            justifyContent: 'flex-start',
            color: '#6b7280',
            textTransform: 'none',
            fontWeight: 800,
          }}
        >
          Limpiar filtros
        </Button>
      </Stack>
    </Box>
  );
}

/* ===================================================================== */
/*  Hero categoría                                                       */
/* ===================================================================== */

function CategoryHero({
  categoria,
  colorPrimario,
}) {
  const [bannerError, setBannerError] =
    useState(false);

  const banner =
    cleanString(categoria?.banner);

  useEffect(() => {
    setBannerError(false);
  }, [banner]);

  const hasBanner =
    Boolean(banner) && !bannerError;

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: {
          xs: 0,
          sm: 4,
        },
        minHeight: {
          xs: hasBanner ? 250 : 210,
          md: hasBanner ? 330 : 250,
        },
        bgcolor: '#111827',
        display: 'flex',
        alignItems: 'flex-end',
      }}
    >
      {hasBanner ? (
        <Box
          component="img"
          src={banner}
          alt={`Banner ${categoria?.nombre || 'categoría'}`}
          onError={() =>
            setBannerError(true)
          }
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      ) : (
        <>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                `linear-gradient(135deg, ${colorPrimario} 0%, #111827 72%)`,
            }}
          />

          <CategoryIcon
            sx={{
              position: 'absolute',
              right: {
                xs: 18,
                md: 42,
              },
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: {
                xs: 130,
                md: 200,
              },
              color: '#fff',
              opacity: 0.08,
            }}
          />
        </>
      )}

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            hasBanner
              ? 'linear-gradient(90deg, rgba(15,23,42,0.92) 0%, rgba(15,23,42,0.66) 52%, rgba(15,23,42,0.20) 100%)'
              : 'linear-gradient(0deg, rgba(15,23,42,0.20), rgba(15,23,42,0.05))',
        }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: 780,
          p: {
            xs: 2.5,
            sm: 4,
            md: 5,
          },
        }}
      >
        <Chip
          label="Categoría"
          size="small"
          sx={{
            mb: 1.5,
            bgcolor: 'rgba(255,255,255,0.14)',
            color: '#fff',
            border:
              '1px solid rgba(255,255,255,0.22)',
            fontWeight: 800,
            backdropFilter: 'blur(8px)',
          }}
        />

        <Typography
          component="h1"
          sx={{
            color: '#fff',
            fontWeight: 950,
            lineHeight: 1.05,
            fontSize: {
              xs: 32,
              sm: 42,
              md: 52,
            },
            letterSpacing: '-0.025em',
          }}
        >
          {categoria?.nombre || 'Categoría'}
        </Typography>

        {categoria?.descripcion ? (
          <Typography
            sx={{
              mt: 1.5,
              maxWidth: 680,
              color: 'rgba(255,255,255,0.88)',
              fontSize: {
                xs: 15,
                md: 17,
              },
              lineHeight: 1.65,
            }}
          >
            {categoria.descripcion}
          </Typography>
        ) : null}

        <Typography
          sx={{
            mt: 1.6,
            color: 'rgba(255,255,255,0.74)',
            fontSize: 14,
            fontWeight: 800,
          }}
        >
          {Number(categoria?.productos || 0)
            .toLocaleString('es-CL')} productos
        </Typography>
      </Box>
    </Paper>
  );
}

/* ===================================================================== */
/*  Página                                                               */
/* ===================================================================== */

export default function Categoria({
  tienda,
  store,
  storeConfig,
}) {
  const { slug = '' } = useParams();
  const [searchParams, setSearchParams] =
    useSearchParams();

  const isMobile =
    useMediaQuery('(max-width:899px)');

  const tiendaData =
    useMemo(
      () =>
        resolveTiendaData(
          tienda ||
          store ||
          storeConfig
        ),
      [tienda, store, storeConfig]
    );

  const colorPrimario =
    cleanString(
      tiendaData?.branding?.colorPrimario
    ) || '#1976d2';

  const mostrarStock =
    tiendaData?.ecommerce?.mostrarStock !== false;

  const mostrarSku =
    tiendaData?.ecommerce?.mostrarSku !== false;

  const filters =
    useMemo(
      () => readFilters(searchParams),
      [searchParams]
    );

  const [categoria, setCategoria] =
    useState(null);

  const [categoryLoading, setCategoryLoading] =
    useState(true);

  const [categoryError, setCategoryError] =
    useState('');

  const [products, setProducts] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [productsError, setProductsError] =
    useState('');

  const [filterData, setFilterData] =
    useState({});

  const [filtersLoading, setFiltersLoading] =
    useState(true);

  const [meta, setMeta] =
    useState({
      page: 1,
      limit: PAGE_LIMIT,
      total: 0,
      totalPages: 1,
    });

  const [drawerOpen, setDrawerOpen] =
    useState(false);

  const [searchInput, setSearchInput] =
    useState(filters.q);

  const [localPrecioMin, setLocalPrecioMin] =
    useState(filters.precioMin);

  const [localPrecioMax, setLocalPrecioMax] =
    useState(filters.precioMax);

  useEffect(() => {
    setSearchInput(filters.q);
  }, [filters.q]);

  useEffect(() => {
    setLocalPrecioMin(filters.precioMin);
    setLocalPrecioMax(filters.precioMax);
  }, [
    filters.precioMin,
    filters.precioMax,
  ]);

  /* =================================================================== */
  /* Categoría                                                           */
  /* =================================================================== */

  useEffect(() => {
    let mounted = true;
    const controller =
      new AbortController();

    async function loadCategory() {
      const safeSlug = cleanString(slug);

      if (!safeSlug) {
        setCategoria(null);
        setCategoryError(
          'La categoría solicitada no es válida.'
        );
        setCategoryLoading(false);
        return;
      }

      setCategoryLoading(true);
      setCategoryError('');

      try {
        const response = await api.get(
          `/categorias/${encodeURIComponent(safeSlug)}`,
          {
            signal: controller.signal,
          }
        );

        if (!mounted) {
          return;
        }

        const nextCategory =
          extractCategory(response?.data);

        if (!nextCategory?.id) {
          throw new Error(
            'La API devolvió una categoría inválida.'
          );
        }

        setCategoria(nextCategory);
      } catch (error) {
        if (
          error?.name === 'CanceledError' ||
          error?.name === 'AbortError' ||
          error?.code === 'ERR_CANCELED'
        ) {
          return;
        }

        if (!mounted) {
          return;
        }

        setCategoria(null);

        if (error?.response?.status === 404) {
          setCategoryError(
            'La categoría no existe o no está disponible.'
          );
        } else {
          setCategoryError(
            error?.response?.data?.message ||
            error?.message ||
            'No fue posible cargar la categoría.'
          );
        }
      } finally {
        if (mounted) {
          setCategoryLoading(false);
        }
      }
    }

    loadCategory();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [slug]);

  /* =================================================================== */
  /* Catálogos para filtros                                              */
  /* =================================================================== */

  useEffect(() => {
    let mounted = true;
    const controller =
      new AbortController();

    async function loadFilters() {
      setFiltersLoading(true);

      try {
        const response = await api.get(
          '/productos/filtros',
          {
            signal: controller.signal,
          }
        );

        if (!mounted) {
          return;
        }

        setFilterData(
          extractFilters(response?.data)
        );
      } catch (error) {
        if (
          error?.name !== 'CanceledError' &&
          error?.name !== 'AbortError' &&
          error?.code !== 'ERR_CANCELED'
        ) {
          console.warn(
            '[Categoria] No fue posible cargar filtros:',
            error?.message || error
          );
        }
      } finally {
        if (mounted) {
          setFiltersLoading(false);
        }
      }
    }

    loadFilters();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, []);

  /* =================================================================== */
  /* Productos de la categoría                                          */
  /* =================================================================== */

  useEffect(() => {
    if (!categoria?.id) {
      setProducts([]);
      setMeta({
        page: 1,
        limit: PAGE_LIMIT,
        total: 0,
        totalPages: 1,
      });
      return undefined;
    }

    let mounted = true;
    const controller =
      new AbortController();

    async function loadProducts() {
      setLoading(true);
      setProductsError('');

      try {
        const params = {
          categoria_id: categoria.id,
          page: filters.page,
          limit: PAGE_LIMIT,
          orden: filters.orden,
        };

        if (filters.q) {
          params.q = filters.q;
        }

        if (filters.marcaId) {
          params.marca_id = filters.marcaId;
        }

        if (filters.precioMin !== '') {
          params.precio_min = filters.precioMin;
        }

        if (filters.precioMax !== '') {
          params.precio_max = filters.precioMax;
        }

        if (filters.disponible) {
          params.disponible = true;
        }

        if (filters.destacado) {
          params.destacado = true;
        }

        const response = await api.get(
          '/productos',
          {
            params,
            signal: controller.signal,
          }
        );

        if (!mounted) {
          return;
        }

        const payload =
          response?.data || {};

        setProducts(
          extractProducts(payload)
        );

        const nextMeta =
          payload?.meta || {};

        setMeta({
          page:
            Number(
              nextMeta.page ||
              filters.page
            ) || 1,

          limit:
            Number(
              nextMeta.limit ||
              PAGE_LIMIT
            ) || PAGE_LIMIT,

          total:
            Number(
              nextMeta.total || 0
            ),

          totalPages:
            Math.max(
              1,
              Number(
                nextMeta.totalPages ||
                nextMeta.totalPaginas ||
                1
              )
            ),
        });
      } catch (error) {
        if (
          error?.name === 'CanceledError' ||
          error?.name === 'AbortError' ||
          error?.code === 'ERR_CANCELED'
        ) {
          return;
        }

        console.error(
          '[Categoria] Error cargando productos:',
          error
        );

        if (mounted) {
          setProducts([]);
          setProductsError(
            error?.response?.data?.message ||
            'No fue posible cargar los productos de esta categoría.'
          );
          setMeta({
            page: 1,
            limit: PAGE_LIMIT,
            total: 0,
            totalPages: 1,
          });
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadProducts();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [
    categoria?.id,
    filters.q,
    filters.marcaId,
    filters.precioMin,
    filters.precioMax,
    filters.disponible,
    filters.destacado,
    filters.orden,
    filters.page,
  ]);

  /* =================================================================== */
  /* Cambios de filtros                                                  */
  /* =================================================================== */

  const replaceFilters =
    useCallback(
      (nextFilters, replace = false) => {
        setSearchParams(
          filtersToSearchParams(nextFilters),
          { replace }
        );
      },
      [setSearchParams]
    );

  const updateFilter =
    useCallback(
      (key, value) => {
        replaceFilters({
          ...filters,
          [key]: value,
          page: 1,
        });
      },
      [filters, replaceFilters]
    );

  const resetFilters =
    useCallback(() => {
      setDrawerOpen(false);
      setSearchInput('');
      setLocalPrecioMin('');
      setLocalPrecioMax('');

      replaceFilters({
        ...DEFAULT_FILTERS,
      });
    }, [replaceFilters]);

  const applyPrice =
    useCallback(() => {
      replaceFilters({
        ...filters,
        precioMin:
          cleanString(localPrecioMin),
        precioMax:
          cleanString(localPrecioMax),
        page: 1,
      });
    }, [
      filters,
      localPrecioMin,
      localPrecioMax,
      replaceFilters,
    ]);

  const submitSearch =
    useCallback(
      (event) => {
        event?.preventDefault?.();

        replaceFilters({
          ...filters,
          q: cleanString(searchInput),
          page: 1,
        });
      }, [
        filters,
        searchInput,
        replaceFilters,
      ]
    );

  const handlePageChange =
    useCallback(
      (_event, page) => {
        replaceFilters({
          ...filters,
          page,
        });

        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'auto',
        });
      },
      [filters, replaceFilters]
    );

  /* =================================================================== */
  /* Chips activos                                                       */
  /* =================================================================== */

  const activeChips =
    useMemo(() => {
      const chips = [];

      if (filters.q) {
        chips.push({
          key: 'q',
          label: `Búsqueda: ${filters.q}`,
        });
      }

      if (filters.marcaId) {
        const marca =
          filterData?.marcas?.find(
            (item) =>
              String(item.id) ===
              String(filters.marcaId)
          );

        chips.push({
          key: 'marcaId',
          label:
            marca?.nombre
              ? `Marca: ${marca.nombre}`
              : 'Marca seleccionada',
        });
      }

      if (
        filters.precioMin !== '' ||
        filters.precioMax !== ''
      ) {
        const desde =
          filters.precioMin !== ''
            ? formatPrice(filters.precioMin)
            : 'sin mínimo';

        const hasta =
          filters.precioMax !== ''
            ? formatPrice(filters.precioMax)
            : 'sin máximo';

        chips.push({
          key: 'precio',
          label: `Precio: ${desde} – ${hasta}`,
        });
      }

      if (filters.disponible) {
        chips.push({
          key: 'disponible',
          label: 'Sólo disponibles',
        });
      }

      if (filters.destacado) {
        chips.push({
          key: 'destacado',
          label: 'Sólo destacados',
        });
      }

      return chips;
    }, [filters, filterData]);

  const removeChip =
    useCallback(
      (key) => {
        if (key === 'precio') {
          setLocalPrecioMin('');
          setLocalPrecioMax('');

          replaceFilters({
            ...filters,
            precioMin: '',
            precioMax: '',
            page: 1,
          });

          return;
        }

        if (key === 'q') {
          setSearchInput('');
        }

        replaceFilters({
          ...filters,
          [key]:
            key === 'disponible' ||
            key === 'destacado'
              ? false
              : '',
          page: 1,
        });
      },
      [filters, replaceFilters]
    );

  const filtersPanel = (
    <FiltersPanel
      filters={filters}
      filterData={filterData}
      localPrecioMin={localPrecioMin}
      localPrecioMax={localPrecioMax}
      setLocalPrecioMin={setLocalPrecioMin}
      setLocalPrecioMax={setLocalPrecioMax}
      updateFilter={updateFilter}
      applyPrice={applyPrice}
      resetFilters={resetFilters}
      onClose={
        isMobile
          ? () => setDrawerOpen(false)
          : null
      }
    />
  );

  /* =================================================================== */
  /* Estados categoría                                                   */
  /* =================================================================== */

  if (categoryLoading) {
    return (
      <Box sx={{ bgcolor: '#f8fafc', minHeight: '70vh' }}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Skeleton
            variant="rounded"
            height={300}
            sx={{ borderRadius: 4 }}
          />

          <Grid container spacing={2.5} sx={{ mt: 1 }}>
            {Array.from({ length: 8 }).map((_, index) => (
              <Grid
                item
                xs={6}
                sm={4}
                md={3}
                key={index}
              >
                <Skeleton
                  variant="rounded"
                  sx={{
                    width: '100%',
                    aspectRatio: '0.72 / 1',
                    borderRadius: 3,
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  }

  if (!categoria) {
    return (
      <Box
        sx={{
          bgcolor: '#f8fafc',
          minHeight: '70vh',
          py: 6,
        }}
      >
        <Container maxWidth="md">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: 4,
              border: '1px solid #e5e7eb',
              textAlign: 'center',
            }}
          >
            <CategoryIcon
              sx={{
                fontSize: 70,
                color: '#cbd5e1',
              }}
            />

            <Typography
              component="h1"
              sx={{
                mt: 1.5,
                fontSize: 26,
                fontWeight: 950,
                color: '#111827',
              }}
            >
              Categoría no disponible
            </Typography>

            <Typography
              sx={{
                mt: 1,
                color: '#6b7280',
              }}
            >
              {categoryError ||
                'La categoría solicitada no se encuentra disponible.'}
            </Typography>

            <Button
              component={RouterLink}
              to="/productos"
              startIcon={<BackIcon />}
              variant="contained"
              sx={{
                mt: 3,
                textTransform: 'none',
                fontWeight: 900,
              }}
            >
              Ver productos
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '70vh',
        bgcolor: '#f8fafc',
        pb: 7,
      }}
    >
      <Container
        maxWidth="xl"
        disableGutters={isMobile}
        sx={{
          pt: {
            xs: 0,
            sm: 2.5,
          },
        }}
      >
        {/* ============================================================= */}
        {/* Breadcrumb + hero                                             */}
        {/* ============================================================= */}

        <Box
          sx={{
            px: {
              xs: 2,
              sm: 0,
            },
            mb: 1.5,
            display: {
              xs: 'none',
              sm: 'block',
            },
          }}
        >
          <Stack
            direction="row"
            spacing={0.8}
            alignItems="center"
          >
            <Typography
              component={RouterLink}
              to="/"
              variant="caption"
              sx={{
                color: '#6b7280',
                textDecoration: 'none',
                fontWeight: 700,
                '&:hover': {
                  color: colorPrimario,
                },
              }}
            >
              Inicio
            </Typography>

            <Typography variant="caption" sx={{ color: '#cbd5e1' }}>
              /
            </Typography>

            <Typography
              component={RouterLink}
              to="/productos"
              variant="caption"
              sx={{
                color: '#6b7280',
                textDecoration: 'none',
                fontWeight: 700,
                '&:hover': {
                  color: colorPrimario,
                },
              }}
            >
              Productos
            </Typography>

            <Typography variant="caption" sx={{ color: '#cbd5e1' }}>
              /
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color: '#374151',
                fontWeight: 900,
              }}
            >
              {categoria.nombre}
            </Typography>
          </Stack>
        </Box>

        <CategoryHero
          categoria={categoria}
          colorPrimario={colorPrimario}
        />

        {/* ============================================================= */}
        {/* Barra de búsqueda / orden                                     */}
        {/* ============================================================= */}

        <Box
          sx={{
            px: {
              xs: 2,
              sm: 0,
            },
            mt: {
              xs: 2.5,
              md: 3,
            },
          }}
        >
          <Stack spacing={2}>
            <Stack
              direction={{
                xs: 'column',
                md: 'row',
              }}
              spacing={1.5}
              alignItems={{
                xs: 'stretch',
                md: 'center',
              }}
              justifyContent="space-between"
            >
              <Box>
                <Typography
                  sx={{
                    color: '#111827',
                    fontWeight: 950,
                    fontSize: {
                      xs: 22,
                      md: 25,
                    },
                  }}
                >
                  Productos
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.25,
                    color: '#6b7280',
                  }}
                >
                  {loading
                    ? 'Actualizando resultados…'
                    : `${meta.total.toLocaleString('es-CL')} resultado${meta.total === 1 ? '' : 's'}`}
                </Typography>
              </Box>

              <Stack
                direction={{
                  xs: 'column',
                  sm: 'row',
                }}
                spacing={1.25}
                sx={{
                  width: {
                    xs: '100%',
                    md: 'auto',
                  },
                }}
              >
                <Box
                  component="form"
                  onSubmit={submitSearch}
                  sx={{
                    width: {
                      xs: '100%',
                      md: 350,
                    },
                  }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    value={searchInput}
                    onChange={(event) =>
                      setSearchInput(
                        event.target.value
                      )
                    }
                    placeholder={`Buscar en ${categoria.nombre}`}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon
                            sx={{ color: '#9ca3af' }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ bgcolor: '#fff' }}
                  />
                </Box>

                {isMobile ? (
                  <Button
                    variant="outlined"
                    startIcon={<FilterIcon />}
                    onClick={() =>
                      setDrawerOpen(true)
                    }
                    sx={{
                      textTransform: 'none',
                      fontWeight: 900,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Filtros
                    {activeChips.length > 0
                      ? ` (${activeChips.length})`
                      : ''}
                  </Button>
                ) : null}

                <FormControl
                  size="small"
                  sx={{
                    minWidth: 205,
                  }}
                >
                  <InputLabel>
                    Ordenar
                  </InputLabel>

                  <Select
                    value={filters.orden}
                    label="Ordenar"
                    onChange={(event) =>
                      updateFilter(
                        'orden',
                        event.target.value
                      )
                    }
                    sx={{ bgcolor: '#fff' }}
                  >
                    {(
                      filterData?.ordenes?.length
                        ? filterData.ordenes
                        : [
                            {
                              value: 'relevancia',
                              label: 'Relevancia',
                            },
                            {
                              value: 'precio_asc',
                              label: 'Precio: menor a mayor',
                            },
                            {
                              value: 'precio_desc',
                              label: 'Precio: mayor a menor',
                            },
                            {
                              value: 'nombre_asc',
                              label: 'Nombre: A-Z',
                            },
                            {
                              value: 'recientes',
                              label: 'Más recientes',
                            },
                          ]
                    ).map((item) => (
                      <MenuItem
                        key={item.value}
                        value={item.value}
                      >
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Stack>

            {activeChips.length > 0 ? (
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                flexWrap="wrap"
                alignItems="center"
              >
                {activeChips.map((chip) => (
                  <Chip
                    key={chip.key}
                    label={chip.label}
                    onDelete={() =>
                      removeChip(chip.key)
                    }
                    sx={{
                      bgcolor: '#fff',
                      border: '1px solid #e5e7eb',
                    }}
                  />
                ))}

                <Button
                  size="small"
                  onClick={resetFilters}
                  sx={{
                    color: '#6b7280',
                    textTransform: 'none',
                    fontWeight: 800,
                  }}
                >
                  Limpiar todo
                </Button>
              </Stack>
            ) : null}
          </Stack>

          {/* =========================================================== */}
          {/* Resultados + filtros                                        */}
          {/* =========================================================== */}

          <Grid
            container
            spacing={3}
            sx={{ mt: 0.5 }}
          >
            {!isMobile ? (
              <Grid
                item
                md={3}
                lg={2.5}
              >
                <Paper
                  elevation={0}
                  sx={{
                    position: 'sticky',
                    top: 104,
                    p: 2.25,
                    borderRadius: 3,
                    border: '1px solid #e5e7eb',
                    bgcolor: '#fff',
                  }}
                >
                  {filtersLoading ? (
                    <Stack spacing={2}>
                      <Skeleton height={35} />
                      <Skeleton height={45} />
                      <Skeleton height={80} />
                    </Stack>
                  ) : (
                    filtersPanel
                  )}
                </Paper>
              </Grid>
            ) : null}

            <Grid
              item
              xs={12}
              md={9}
              lg={9.5}
            >
              {productsError ? (
                <Alert
                  severity="warning"
                  sx={{ mb: 2 }}
                >
                  {productsError}
                </Alert>
              ) : null}

              {loading ? (
                <Grid
                  container
                  spacing={2.5}
                >
                  {Array.from({ length: 8 }).map((_, index) => (
                    <Grid
                      item
                      xs={6}
                      sm={4}
                      lg={3}
                      key={index}
                    >
                      <Skeleton
                        variant="rounded"
                        sx={{
                          width: '100%',
                          aspectRatio: '0.72 / 1',
                          borderRadius: 3,
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              ) : products.length > 0 ? (
                <>
                  <Grid
                    container
                    spacing={2.5}
                  >
                    {products.map((product) => (
                      <Grid
                        item
                        xs={6}
                        sm={4}
                        lg={3}
                        key={product.id}
                      >
                        <ProductCard
                          product={product}
                          colorPrimario={colorPrimario}
                          mostrarStock={mostrarStock}
                          mostrarSku={mostrarSku}
                        />
                      </Grid>
                    ))}
                  </Grid>

                  {meta.totalPages > 1 ? (
                    <Stack
                      alignItems="center"
                      sx={{
                        mt: {
                          xs: 4,
                          md: 5,
                        },
                      }}
                    >
                      <Pagination
                        page={meta.page}
                        count={meta.totalPages}
                        onChange={handlePageChange}
                        color="primary"
                        size={
                          isMobile
                            ? 'small'
                            : 'medium'
                        }
                        siblingCount={
                          isMobile
                            ? 0
                            : 1
                        }
                      />
                    </Stack>
                  ) : null}
                </>
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    py: 8,
                    px: 3,
                    borderRadius: 3,
                    border: '1px solid #e5e7eb',
                    bgcolor: '#fff',
                    textAlign: 'center',
                  }}
                >
                  <ProductIcon
                    sx={{
                      fontSize: 64,
                      color: '#cbd5e1',
                    }}
                  />

                  <Typography
                    sx={{
                      mt: 1.5,
                      fontWeight: 900,
                      fontSize: 21,
                      color: '#111827',
                    }}
                  >
                    No encontramos productos
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.75,
                      color: '#6b7280',
                    }}
                  >
                    No hay resultados con los filtros seleccionados en {categoria.nombre}.
                  </Typography>

                  {activeChips.length > 0 ? (
                    <Button
                      startIcon={<ResetIcon />}
                      onClick={resetFilters}
                      sx={{
                        mt: 2,
                        textTransform: 'none',
                        fontWeight: 800,
                        color: colorPrimario,
                      }}
                    >
                      Limpiar filtros
                    </Button>
                  ) : null}
                </Paper>
              )}
            </Grid>
          </Grid>

          {/* =========================================================== */}
          {/* Contenido editorial inferior                                */}
          {/* =========================================================== */}

          {categoria.descripcion ? (
            <Paper
              component="section"
              elevation={0}
              sx={{
                mt: 5,
                p: {
                  xs: 2.5,
                  md: 4,
                },
                borderRadius: 4,
                border: '1px solid #e5e7eb',
                bgcolor: '#fff',
              }}
            >
              <Typography
                component="h2"
                sx={{
                  fontSize: 22,
                  fontWeight: 950,
                  color: '#111827',
                }}
              >
                Acerca de {categoria.nombre}
              </Typography>

              <Typography
                sx={{
                  mt: 1.2,
                  color: '#4b5563',
                  lineHeight: 1.75,
                  whiteSpace: 'pre-line',
                }}
              >
                {categoria.descripcion}
              </Typography>
            </Paper>
          ) : null}
        </Box>
      </Container>

      {/* =============================================================== */}
      {/* Drawer móvil                                                    */}
      {/* =============================================================== */}

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() =>
          setDrawerOpen(false)
        }
        PaperProps={{
          sx: {
            width: 'min(90vw, 360px)',
            p: 2.5,
          },
        }}
      >
        {filtersLoading ? (
          <Stack spacing={2}>
            <Skeleton height={35} />
            <Skeleton height={45} />
            <Skeleton height={80} />
          </Stack>
        ) : (
          filtersPanel
        )}
      </Drawer>
    </Box>
  );
}
